import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { getQuote } from "@/lib/api";

const QUOTE_DEBOUNCE_MS = 500;
const DEMO_DELAY_MS = 2000;

/** Debounced quote fetching for the exchange pages. */
export function useLiveQuote(action, amount, fromToken, toToken) {
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchQuote = useCallback(async () => {
    setLoading(true);
    try {
      setQuote(await getQuote(action, Number(amount), fromToken, toToken));
      setQuoteError(null);
    } catch (err) {
      setQuote(null);
      setQuoteError(err?.response?.data?.detail || "Could not fetch quote");
    } finally {
      setLoading(false);
    }
  }, [action, amount, fromToken, toToken]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (amount && Number(amount) > 0) {
        fetchQuote();
      } else {
        setQuote(null);
        setQuoteError(null);
      }
    }, QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [amount, fetchQuote]);

  return { quote, setQuote, quoteError, loading };
}

/** Runs a live on-chain trade (or the demo simulation) and tracks progress + the last tx hash. */
export function useTradeExecution({ address, refreshBalances, onDone }) {
  const [executing, setExecuting] = useState(false);
  const [step, setStep] = useState("");
  const [lastTx, setLastTx] = useState(null);

  const runLive = async ({ execute, successMessage, errorMessage }) => {
    setExecuting(true);
    try {
      const hash = await execute(setStep);
      toast.success(successMessage);
      setLastTx(hash);
      onDone();
      setTimeout(() => refreshBalances(), 1500);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setExecuting(false);
      setStep("");
    }
  };

  const runDemo = (message) => {
    setExecuting(true);
    setTimeout(() => {
      toast.success(message);
      setExecuting(false);
      onDone();
    }, DEMO_DELAY_MS);
  };

  const run = ({ live, demoMessage, ...liveArgs }) => {
    if (!address) {
      toast.error("Connect your wallet first");
      return;
    }
    if (live) return runLive(liveArgs);
    runDemo(demoMessage);
  };

  return { executing, step, lastTx, run };
}
